import z from "zod";
import { CommentSchema } from "./Comment";
import { MemberSettingsSchema } from "./MemberSettings";
import { OrganisationSchema } from "./Organisation";

/**
 * MemberType represents the type of member as a string literal.
 *
 * Therapist types: "talking_therapist", "body_therapist", "both" — the latter when a member
 * provides both talking and body therapy. Legacy values "therapist" and "bodyworker" persisted
 * in older Mongo documents are normalised on read by `MemberTypeSchema` below — they map to
 * "talking_therapist" and "body_therapist" respectively. New writes always use the new names.
 *
 * @example
 * const type: MemberType = 'talking_therapist';
 */
export type MemberType =
  | "talking_therapist"
  | "body_therapist"
  | "both"
  | "system"
  | "admin"
  | "superadmin";

/**
 * Friendly label for the AccountView "Therapist Type" well and similar surfaces.
 *
 * Tolerant of legacy values still resident in older `tpruser` localStorage entries (or any
 * member object that hasn't yet round-tripped through `MemberTypeSchema`). "therapist" maps
 * to "Talking Therapist" and "bodyworker" maps to "Body Therapist".
 */
export function memberTypeForDisplay(type: MemberType | string | undefined | null): string {
  if (!type) return "";
  switch (type) {
    case "talking_therapist":
    case "therapist":
      return "Talking Therapist";
    case "body_therapist":
    case "bodyworker":
      return "Body Therapist";
    case "both":
      return "Both (Talking and Body Therapist)";
    case "admin":
      return "Admin";
    case "superadmin":
      return "Superadmin";
    case "system":
      return "System";
    default:
      return "";
  }
}

/** Zod schema that accepts both legacy and new member-type strings, normalising legacy values. */
export const MemberTypeSchema = z.preprocess(
  (val) => {
    if (val === "therapist") return "talking_therapist";
    if (val === "bodyworker") return "body_therapist";
    return val;
  },
  z.enum(["talking_therapist", "body_therapist", "both", "system", "admin", "superadmin"]),
);

export const MemberSchema = z.object({
  id: z.string(),
  type: MemberTypeSchema,
  email: z.string().email(),
  salt: z.string().optional(),
  userKey: z.string().optional(),
  avatar: z.string().optional(),
  active: z.boolean(),
  // Whether the member is permitted to log in. Together with `active` this is the true login gate
  // (`active && canLogin`). Managed server-side (org invariant, suspension, termination); surfaced
  // to elevated users via makeSafeMemberForElevated. Defaults to true when absent.
  canLogin: z.boolean().optional().catch(true),
  // Whether this member may be selected as the target of an on-behalf booking (e.g. the
  // elevated-user "book for member" search). Restricted members are hidden from non-admin
  // callers server-side via GET /members?excludeUnassignable=true. Defaults to true when absent.
  canBeAssignedBookings: z.boolean().optional().catch(true),
  firstName: z.string(),
  lastName: z.string(),
  businessName: z.string().optional(),
  permissableLocationIds: z.array(z.string()).optional(),
  permissableLocations: z.array(z.any()).optional(), // Replace z.any() with Location schema if available
  practiceManagerLocationIds: z.array(z.string()).optional(),
  practiceManagerLocations: z.array(z.any()).optional(), // Replace z.any() with Location schema if available
  preferredLocations: z.array(z.string()).optional(),
  preferredRooms: z.array(z.string()).optional(),
  role: z.array(z.string()),
  hasMfa: z.boolean().optional(),
  settings: MemberSettingsSchema.optional(),
  dismissedNotifications: z.array(z.string()).optional(),
  comments: CommentSchema.array().optional(),
  token: z.string().optional(),
  loginCount: z.number().optional(),
  organisation: OrganisationSchema.optional(),
  organisationId: z.string().optional(),
  ownerOfOrganisationId: z.string().optional(),
  lastLoginAt: z.union([z.string(), z.coerce.string()]).optional(),
  lastMfaAt: z.union([z.string(), z.coerce.string()]).optional(),
  mfaExpiry: z.union([z.string(), z.coerce.string()]).optional(),
  termsVersion: z.number().optional(),
  annualDeclarationAcceptedAt: z.string().optional(),
  // Login-gate acceptance, carried on the stored member (computed server-side at login, refreshed
  // when the member accepts terms/declaration) so the TPR route guard can enforce the gates.
  termsAccepted: z.boolean().optional(),
  annualDeclarationAccepted: z.boolean().optional(),
  currentTermsVersion: z.number().optional(),
  createdAt: z.union([z.string(), z.coerce.string()]),
  updatedAt: z.union([z.string(), z.coerce.string()]),
  totalBookings: z.number().optional(),
  cancelledBookings: z.number().optional(),

  // Suspension fields
  isSuspended: z.boolean().optional().default(false),
  suspensionEndDate: z.string().optional(),
  suspendedAt: z.string().optional(),
  suspendedBy: z.string().optional(),
  autoTerminationDate: z.string().optional(), // 12 months from suspension start

  // Termination fields
  isTerminated: z.boolean().optional().default(false),
  terminationDate: z.string().optional(),
  terminatedAt: z.string().optional(),
  terminatedBy: z.string().optional(),
  scheduledTerminationId: z.string().optional(),

  // End Membership fields — unified flow replacing standalone Suspend + Terminate.
  pendingMembershipEnd: z.boolean().optional(),
  membershipEndDate: z.string().optional(),
  membershipEndScheduledAt: z.string().optional(),
  membershipEndScheduledBy: z.string().optional(),
  anonymisationDate: z.string().optional(), // ISO date YYYY-MM-DD — membershipEndDate + 1 year, set at termination

  // OfficeRND sync data
  officeRnd: z
    .object({
      id: z.string().optional(),
      status: z.string().optional(),
      isBillingPerson: z.boolean().optional(),
      isContactPerson: z.boolean().optional(),
      location: z.string().optional(),
      syncedAt: z.string().optional(),
    })
    .optional(),
});

export type Member = z.infer<typeof MemberSchema>;

export interface MemberFilters {
  // ids
  memberId?: string;
  permissableLocationIds?: string;
  practiceManagerLocationIds?: string;

  // misc
  email?: string;
  lastName?: string;

  // status
  active?: boolean;
  hasMfa?: boolean;
  isAdmin?: boolean;
  isPracticeManager?: boolean;
  isProspect?: boolean;
  isTherapist?: boolean;
  isBodyworker?: boolean;
  isPendingTermination?: boolean;
  isTerminated?: boolean;

  // text search
  searchTerm?: string;
  organisationName?: string;
}

export interface MemberSort {
  // Must stay identical to ember/src/types/Member.ts — the values are sent as the `sortField`
  // query param and validated against a whitelist there. Adding a field here without adding it
  // there means the server silently ignores it and falls back to its default sort.
  field:
    | "lastName"
    | "firstName"
    | "email"
    | "createdAt"
    | "lastLoginAt"
    | "totalBookings"
    | "cancelledBookings"
    | "roleRank";
  order: "asc" | "desc";
}

export type MemberLoginResponse = {
  message: string;
  member: Member;
  mfa?: {
    code: string;
    expiresAt: string;
  };
  termsAccepted: boolean;
  currentTermsVersion: number;
  annualDeclarationAccepted: boolean;
};
